// v2/components/CheckoutV2.jsx
//
// Página de checkout v2: cotiza el carrito → reserva atómicamente →
// inicia Webpay. Reusa el carrito v1 (services/Carrito) como fuente de
// items, pero todo el cálculo de precio y reserva se hace contra /v2/*.
//
// Diferencias con v1:
//  - Validación BLOQUEANTE de Tarjeta Vecino por cada inscrito.
//  - Precio firmado por el backend (hash sha256), inmutable hasta pago.
//  - Reserva atómica de cupos antes del Webpay (no race condition).
//  - Idempotencia del callback Webpay (manejada en PagoReturnV2).

import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, Form, Input, Button, Typography, Alert, Spin, Space, Steps, Tag
} from 'antd';
import {
  UserOutlined, MailOutlined, PhoneOutlined,
  ArrowLeftOutlined, CreditCardOutlined, LoadingOutlined,
  ShoppingCartOutlined, SafetyCertificateOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import CarritoTaller from '../../../service/Carrito';
import Auth from '../../../service/Auth';
import { useNotify } from '../../../providers/NotificationProvider';
import RutInscritoInput from './RutInscritoInput';
import CotizacionResumen from './CotizacionResumen';
import useCotizacion from '../../../hooks/useCotizacion';
import { codigoError, mensajeDeError } from '../../../service/client';

const { Text } = Typography;

const PASO = {
  CARGANDO: 0,
  DATOS_CLIENTE: 1,
  COTIZAR: 2,
  PAGAR: 3,
  REDIRIGIENDO: 4
};

// 🧪 SOLO local/staging — prefill de datos del titular en el checkout (gated por REACT_APP_ENV).
const MOSTRAR_PREFILL = ['local', 'staging'].includes(process.env.REACT_APP_ENV);
const DATOS_PRUEBA = {
  nombre: process.env.REACT_APP_QUICK_CLIENT_NOMBRE || 'Cliente Prueba',
  rut: process.env.REACT_APP_QUICK_CLIENT_RUT || '11111111-1',
  email: process.env.REACT_APP_QUICK_CLIENT_EMAIL || 'prueba@test.cl',
  telefono: process.env.REACT_APP_QUICK_CLIENT_TEL || '+56911111111',
};

export default function CheckoutV2() {
  const navigate = useNavigate();
  const notify = useNotify();
  const [form] = Form.useForm();

  const [paso, setPaso] = useState(PASO.CARGANDO);
  const [carrito, setCarrito] = useState(null);
  const [bloqueosTV, setBloqueosTV] = useState({}); // { rut: true }
  const [iniciandoPago, setIniciandoPago] = useState(false);

  const { cotizando, cotizacion, error, cotizar, iniciarPago, reset } = useCotizacion();

  useEffect(() => { cargarInicial(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function cargarInicial() {
    try {
      const [user, c] = await Promise.all([Auth.Me(), CarritoTaller.ObtenerCarrito()]);
      if (!c?.items?.length) {
        notify.warning('Tu carrito está vacío');
        navigate('/carrito');
        return;
      }
      setCarrito(c);
      form.setFieldsValue({
        nombre: user?.name || user?.dynamoProfile?.name || '',
        email: user?.email || '',
        telefono: user?.phone || user?.dynamoProfile?.phone || '',
        rut: user?.rut || user?.dynamoProfile?.rut || ''
      });
      setPaso(PASO.DATOS_CLIENTE);
    } catch (err) {
      notify.error(mensajeDeError(err, 'No se pudo cargar el carrito'));
      navigate('/carrito');
    }
  }

  // Items de cotización en formato del backend v2
  const itemsV2 = useMemo(() => buildItemsV2(carrito), [carrito]);

  const algunInscritoBloqueado = Object.values(bloqueosTV).some(Boolean);

  async function handleCotizar() {
    try {
      const datos = await form.validateFields();
      const datosCliente = {
        nombre: datos.nombre,
        rut: datos.rut,
        email: datos.email,
        telefono: datos.telefono,
        comuna: 'Ñuñoa',
        ciudad: 'Santiago'
      };
      await cotizar({
        items: itemsV2,
        datosCliente,
        carritoId: carrito._id || carrito.id
      });
      setPaso(PASO.PAGAR);
    } catch (err) {
      // Si vino de validación de form, no es un error de service
      if (err?.errorFields) return;
      const code = codigoError(err);
      if (code === 'TV_API_NO_RESPONDE') {
        // El input ya muestra el mensaje. Aquí solo aviso al notify.
        notify.error('No pudimos verificar la Tarjeta Vecino. Intenta de nuevo en unos segundos.');
      } else {
        notify.error(mensajeDeError(err, 'No se pudo cotizar tu compra'));
      }
    }
  }

  async function handlePagar() {
    try {
      setIniciandoPago(true);
      setPaso(PASO.REDIRIGIENDO);
      const datos = form.getFieldsValue();
      const returnUrl = `${window.location.origin}/v2/checkout/return`;
      const out = await iniciarPago({
        returnUrl,
        datosCliente: {
          nombre: datos.nombre, rut: datos.rut, email: datos.email,
          telefono: datos.telefono, comuna: 'Ñuñoa', ciudad: 'Santiago'
        }
      });
      if (out.requiereWebpay === false) {
        // Orden $0 (todo gratuito): el backend ya confirmó, no hay Webpay que iniciar.
        notify.success('¡Inscripción confirmada exitosamente!');
        navigate('/mis-talleres');
        return;
      }
      // Webpay devuelve token+url. Construir form auto-submit POST.
      submitWebpayForm(out.url, out.token);
    } catch (err) {
      setIniciandoPago(false);
      setPaso(PASO.PAGAR);
      notify.error(mensajeDeError(err, 'No se pudo iniciar el pago'));
    }
  }

  if (paso === PASO.CARGANDO) {
    return (
      <Centrado>
        <Card style={{ minWidth: 360, textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            {/* h4 plano: mismo motivo que el header (evita el loop de antd Typography en React 19). */}
            <h4 style={{ fontSize: 20, fontWeight: 600, margin: 0, color: 'rgba(0,0,0,0.88)' }}>Cargando tu carrito…</h4>
          </div>
        </Card>
      </Centrado>
    );
  }

  if (paso === PASO.REDIRIGIENDO) {
    return (
      <Centrado>
        <Card style={{ minWidth: 360, textAlign: 'center' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} size="large" />
          <h3 style={{ fontSize: 24, fontWeight: 600, margin: '24px 0 8px', color: 'rgba(0,0,0,0.88)' }}>Redirigiendo a Webpay…</h3>
          <Alert
            type="info" showIcon
            message="No cierres esta ventana"
            description="Serás redirigido a la plataforma de pago seguro."
          />
        </Card>
      </Centrado>
    );
  }

  return (
    <div style={{ minHeight: '100vh', padding: '32px 0' }}>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 16px' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/carrito')} style={{ marginBottom: 16 }}>
          Volver al carrito
        </Button>
        {/* h2 plano en vez de antd Title: evita el loop de EllipsisMeasure/ResizeObserver
            de antd Typography bajo React 19 (crasheaba el checkout con "Maximum update depth"). */}
        <h2 style={{ fontSize: 24, fontWeight: 600, margin: '0 0 8px', color: 'rgba(0,0,0,0.88)' }}>
          <ShoppingCartOutlined /> Confirmar tu inscripción
          <Tag color="blue" style={{ marginLeft: 12, verticalAlign: 'middle' }}>v2</Tag>
        </h2>

        <Steps
          current={paso === PASO.DATOS_CLIENTE ? 0 : paso === PASO.COTIZAR ? 1 : 2}
          style={{ marginBottom: 24 }}
          items={[
            { title: 'Datos del cliente' },
            { title: 'Verificación' },
            { title: 'Pago' }
          ]}
        />

        <Form form={form} layout="vertical">
          <Card title={<><UserOutlined /> Datos del titular</>} style={{ marginBottom: 16 }}>
            <Form.Item label="Nombre completo" name="nombre" rules={[{ required: true, message: 'Ingresa tu nombre' }]}>
              <Input size="large" prefix={<UserOutlined />} />
            </Form.Item>
            <Form.Item label="RUT" name="rut" rules={[{ required: true, message: 'Ingresa tu RUT' }]} extra="Validamos tu Tarjeta Vecino para aplicar descuentos.">
              {/* Para el titular reusamos la misma validación bloqueante */}
              <RutInscritoInput
                onResultado={(r) => { /* no-op por ahora; el descuento aplica solo si el inscrito es vecino */ }}
                onBloqueado={(b) => setBloqueosTV(prev => ({ ...prev, titular: b }))}
              />
            </Form.Item>
            <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email', message: 'Email inválido' }]}>
              <Input size="large" prefix={<MailOutlined />} />
            </Form.Item>
            <Form.Item label="Teléfono" name="telefono" rules={[{ required: true, message: 'Ingresa un teléfono' }]}>
              <Input size="large" prefix={<PhoneOutlined />} />
            </Form.Item>
          </Card>

          {/* Inscritos del carrito (uno por item) */}
          {itemsV2.some(i => i.tipo === 'taller') && (
            <Card title="Inscritos" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {itemsV2.map((it, idx) => (
                  it.tipo !== 'taller' ? null : (
                    <InscritoBloque
                      key={idx}
                      item={it}
                      onBloqueado={(rut, b) => setBloqueosTV(prev => ({ ...prev, [`insc-${idx}`]: b }))}
                    />
                  )
                ))}
              </Space>
            </Card>
          )}

          {paso === PASO.PAGAR && cotizacion && (
            <CotizacionResumen cotizacion={cotizacion} />
          )}

          {error?.code === 'CUPO_AGOTADO' && (
            <Alert
              type="error" showIcon style={{ marginTop: 16 }}
              message="Cupo agotado"
              description="El cupo se agotó mientras estabas comprando. Vuelve al carrito para elegir otro horario."
            />
          )}
        </Form>

        {MOSTRAR_PREFILL && paso === PASO.DATOS_CLIENTE && (
          <Button size="large" block style={{ marginTop: 8 }}
            onClick={() => form.setFieldsValue(DATOS_PRUEBA)}>
            🧪 Rellenar datos de prueba
          </Button>
        )}

        <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
          {paso === PASO.DATOS_CLIENTE && (
            <Button
              type="primary" size="large" block
              icon={<SafetyCertificateOutlined />}
              loading={cotizando}
              disabled={algunInscritoBloqueado}
              onClick={handleCotizar}
            >
              {algunInscritoBloqueado
                ? 'Esperando verificación de Tarjeta Vecino…'
                : 'Continuar al pago'}
            </Button>
          )}

          {paso === PASO.PAGAR && (
            <>
              <Button size="large" onClick={() => { reset(); setPaso(PASO.DATOS_CLIENTE); }} block>
                Volver
              </Button>
              <Button
                type="primary" size="large" block
                icon={<CreditCardOutlined />}
                loading={iniciandoPago}
                onClick={handlePagar}
              >
                {cotizacion?.total > 0
                  ? `Pagar $${cotizacion.total.toLocaleString('es-CL')} con Webpay`
                  : 'Confirmar inscripción gratuita'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InscritoBloque({ item, onBloqueado }) {
  return (
    <Card size="small" style={{ background: '#fafafa' }}>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Text strong>{item.tallerNombre}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Inscrito: {item.inscrito?.nombre || '(sin nombre)'}
        </Text>
        <RutInscritoInput
          value={item.inscrito?.rut}
          autoValidar
          onBloqueado={(b) => onBloqueado?.(item.inscrito?.rut, b)}
          disabled // RUT del inscrito no se edita aquí — viene del carrito
        />
      </Space>
    </Card>
  );
}

function Centrado({ children }) {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {children}
    </div>
  );
}

/**
 * Convierte items del carrito v1 al formato esperado por /v2/cotizar.
 */
function buildItemsV2(carrito) {
  if (!carrito?.items) return [];
  return carrito.items.map((it) => {
    if (it.tipo === 'matricula' || it.datosMatricula) {
      return {
        tipo: 'matricula',
        recinto: it.datosMatricula?.recintoId || it.recinto,
        inscrito: {
          rut: it.inscrito?.rut || it.datosMatricula?.usuarioData?.rut,
          nombre: it.inscrito?.nombre || it.datosMatricula?.usuarioData?.nombre,
          email: it.inscrito?.email || it.datosMatricula?.usuarioData?.email,
          telefono: it.inscrito?.telefono || it.datosMatricula?.usuarioData?.telefono
        }
      };
    }
    return {
      tipo: 'taller',
      taller: it.taller?._id || it.taller,
      tallerNombre: it.taller?.nombre || it.tallerNombre,
      horario: it.horario?._id || it.horario,
      plan: it.planSeleccionado?.plan,
      inscrito: {
        esUsuarioPrincipal: it.inscrito?.esUsuarioPrincipal,
        familiarId: it.inscrito?.familiarId,
        nombre: it.inscrito?.nombre,
        rut: it.inscrito?.rut,
        email: it.inscrito?.email,
        telefono: it.inscrito?.telefono,
        edad: it.inscrito?.edad,
        fechaNacimiento: it.inscrito?.fechaNacimiento,
        genero: it.inscrito?.genero
      }
    };
  });
}

/**
 * Construye un formulario auto-submit POST a Webpay con el token,
 * idéntico al patrón de v1 pero con la URL real de Transbank.
 */
function submitWebpayForm(url, token) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'token_ws';
  input.value = token;
  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
}
